// ─────────────────────────────────────────────────────────────
// Measurement Protocol — SHARED UI BUILDING BLOCKS
//
// Extracted from the original monolithic MPCreationIndex.js so the
// Measurement Protocol module can be split across an index (list) page,
// a creation panel, an expanded-row tab container, and one file per tab.
// Nothing in this file changes any UI or behavior from the original —
// it is a pure "move", plus one bug fix (see `Btn` below).
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { isSmileIntegrated } from "../../../Common/UIComponents";

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    // Some endpoints wrap the list, e.g. { data: [...] } or { items: [...] }
    const nested = data.data ?? data.items ?? data.list ?? data.result;
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const BRAND = "#08757C";
const BRAND_DARK = "#065c61";
const BRAND_GRAD = "linear-gradient(135deg,#08757C,#0a9aa3)";
// Submit uses a darker variant of the same brand gradient so it stays on
// theme with the rest of the page while still reading as visually
// distinct from the (lighter, flat) Save button.
const SUBMIT_GRAD = "linear-gradient(135deg,#065c61,#08757C)";
// Input skins — matched to the reusable InputField / SelectField styling
// used on every other form page: slate-50 background, slate-200 border,
// rounded-lg, primary focus ring. Heights are kept so existing dense tab
// grids don't shift.
const iCls =
  "w-full h-11 bg-slate-50 rounded-lg px-4 text-sm font-medium text-slate-700 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#08757C]/20 focus:border-[#08757C] transition-all placeholder:text-[#9eabac]";
const eiCls =
  "w-full h-10 bg-slate-50 rounded-lg px-3 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#08757C]/20 focus:border-[#08757C] transition-all placeholder:text-[#9eabac]";

const TABS = [
  { key: "testinfo", label: "Test Info" },
  { key: "mchain", label: "Measurement Chain" },
  { key: "dchain", label: "DUT Chain" },
  { key: "comm", label: "Communication/Result" },
  { key: "quest", label: "Questionnaires" },
];

const STATUS_BADGE = {
  Inprogress: "bg-teal-50  text-teal-700  border border-teal-200",
  Created: "bg-slate-100 text-slate-500 border border-slate-200",
  Approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Submitted: "bg-blue-50  text-blue-700  border border-blue-200",
};

// ─────────────────────────────────────────────────────────────
// SHARED TINY COMPONENTS
// ─────────────────────────────────────────────────────────────
// Field label — matched to the reusable InputField / SelectField label
// used on every other form page (slate-500 uppercase, red-500 asterisk).
function Lbl({ children, required }) {
  return (
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

function Badge({ status }) {
  const cls =
    STATUS_BADGE[status] ??
    "bg-slate-100 text-slate-500 border border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold ${cls}`}
    >
      {status || "—"}
    </span>
  );
}

function Btn({ icon, onClick, title, danger = false, disabled = false }) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={(e) => {
        // Rows this button sits in (e.g. the MP list row) are themselves
        // clickable to expand/collapse. Without stopPropagation, clicking
        // this button also bubbles up to the row's onClick, firing the
        // toggle a SECOND time and instantly closing what it just opened
        // — this was why the Edit icon looked like it "didn't expand".
        e.stopPropagation();
        onClick?.(e);
      }}
      className={`h-7 w-7 flex items-center justify-center rounded-lg border transition-all active:scale-95 disabled:opacity-40
        ${
          danger
            ? "border-red-200 bg-red-50 text-red-500 hover:bg-red-100"
            : "border-[#e5e9e9] bg-[#f7fafa] text-[#6e797a] hover:bg-[#e6f4f4] hover:text-[#08757C] hover:border-[#08757C]/30"
        }`}
    >
      <span className="material-symbols-outlined text-[14px]">{icon}</span>
    </button>
  );
}

// A circular expand/collapse toggle — red "−" when open, green "+" when closed.
// Used for both the Test Overview accordion rows and the MP accordion rows,
// matching the two-level nested table in the reference screenshot.
function ExpandToggle({ open, onClick, size = "h-6 w-6" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`${size} flex items-center justify-center rounded-full border transition-all flex-shrink-0
        ${
          open
            ? "border-red-300 bg-red-50 text-red-500 hover:bg-red-100"
            : "border-emerald-300 bg-emerald-50 text-emerald-500 hover:bg-emerald-100"
        }`}
    >
      <span className="material-symbols-outlined text-[14px]">
        {open ? "remove" : "add"}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// FORM-STYLE SHARED COMPONENTS — card sections, icon-prefixed
// fields, checklist panels and toggles, modeled on the
// "Create New Associate" reference design.
// ─────────────────────────────────────────────────────────────
function SectionIcon({ icon }) {
  return (
    <div className="h-8 w-8 rounded-lg bg-teal-50 text-[#08757C] flex items-center justify-center flex-shrink-0">
      <span className="material-symbols-outlined text-[17px]">{icon}</span>
    </div>
  );
}

// Section supports a CONTROLLED open state (`open` + `onOpenChange`) in
// addition to its original uncontrolled mode (`defaultOpen`). This lets
// a parent tab force a collapsed section open — e.g. when Submit finds a
// required field hidden inside a collapsed "Monitoring & Results" panel
// — without changing the behavior for any existing uncontrolled usage.
function Section({
  icon,
  title,
  action,
  children,
  className = "",
  collapsible = false,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next) => {
    const value = typeof next === "function" ? next(open) : next;
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };
  const showBody = collapsible ? open : true;
  return (
    <div
      className={`bg-white rounded-2xl border border-[#e5e9e9] shadow-sm w-full min-w-0 ${className}`}
    >
      <div
        className={`flex items-center gap-2.5 px-5 py-3.5 ${showBody ? "border-b border-[#f1f4f4]" : ""} ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setOpen((v) => !v) : undefined}
      >
        <SectionIcon icon={icon} />
        <h3 className="text-[13.5px] font-extrabold text-[#08757C]">{title}</h3>
        {action && (
          <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
            {action}
          </div>
        )}
        {collapsible && (
          <span
            className={`material-symbols-outlined text-[18px] text-[#9eabac] transition-transform flex-shrink-0 ${action ? "ml-2" : "ml-auto"} ${open ? "" : "-rotate-90"}`}
          >
            expand_more
          </span>
        )}
      </div>
      {showBody && <div className="p-5 min-w-0">{children}</div>}
    </div>
  );
}

// Field label styled like the reference form (dark slate, small caps)
function FLbl({ children, required }) {
  return (
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

const iconInputCls =
  "w-full h-11 bg-slate-50 border rounded-lg pl-10 pr-3 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#08757C]/20 focus:border-[#08757C] transition-all placeholder:text-[#9eabac]";

// Wraps a text/number/date input with a left-aligned icon, plus an
// optional helper line below (validation hint, error, char counter)
function IconField({ icon, hint, counter, error, children }) {
  return (
    <div>
      <div className={`relative `}>
        {icon && (
          <span className="material-symbols-outlined text-[16px] text-[#9eabac] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 ">
            {icon}
          </span>
        )}
        {children}
      </div>
      {(hint || counter) && (
        <div className="flex items-center justify-between mt-1 min-h-[14px]">
          {hint ? (
            <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-1">
              <span
                className="material-symbols-outlined text-[12px]"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                check_circle
              </span>
              {hint}
            </span>
          ) : (
            <span />
          )}
          {counter && (
            <span className="text-[10px] text-[#9eabac] font-medium">
              {counter}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DROPDOWN PORTAL — renders a dropdown's option panel into
// document.body instead of as an `absolute` child of its trigger.
// ─────────────────────────────────────────────────────────────
function useDropdownPosition(anchorRef, open) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      try {
        const r = anchorRef.current?.getBoundingClientRect();
        if (r) setPos({ top: r.bottom + 4, left: r.left, width: r.width });
      } catch (err) {
        console.error("Error positioning dropdown:", err);
      }
    };
    update();
    // capture=true so this also fires on scroll inside nested tables,
    // not just the window itself
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef]);
  return pos;
}

function DropdownPortal({ open, anchorRef, children }) {
  const pos = useDropdownPosition(anchorRef, open);
  if (!open) return null;
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="dp-portal-menu"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────
// AutoComplete
//
// Whether this behaves as a free-text search-as-you-type autocomplete
// or as a plain click-to-select dropdown is controlled by TWO things,
// combined:
//
//   1. `isAutoComplete` prop (defaults to true — the original behaviour
//      this component always had). Pass `isAutoComplete={false}` to force
//      plain-dropdown behaviour for a particular field.
//   2. Smile integration: if this app is currently running embedded
//      inside Smile (`isFromSmile === "1"`), autocomplete is NEVER
//      allowed — it always falls back to the plain dropdown, no matter
//      what `isAutoComplete` was passed as. When running standalone
//      (not embedded in Smile), the `isAutoComplete` prop is honoured
//      as-is.
//
// In both modes `value` / `onChange` keep the exact same shape as
// before ({ value, label } for single-select, an array of those for
// multiple), so every page/tab using this component — and every
// save/get call built around that shape — keeps working unchanged.
// ─────────────────────────────────────────────────────────────
function AutoComplete({
  value,
  onChange,
  options = [],
  placeholder = "Type to search…",
  icon = "search",
  multiple = false, // Pass true for multi-select, false for single
  isAutoComplete = true, // Pass false to force plain dropdown behaviour
  error = "",
}) {
  // Smile-embedded sessions never get free-text autocomplete, regardless
  // of what the caller asked for. Standalone sessions honour the flag.
  const allowAutoComplete = isAutoComplete && !isSmileIntegrated();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        !e.target.closest(".dp-portal-menu")
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const isSelected = (val) =>
    multiple ? value?.find((s) => s.value === val) : value?.value === val;
  const selectedObj = multiple ? value : value;

  // Autocomplete mode: filtered as the user types into the field itself.
  // Dropdown mode: filtered by the small search box inside the panel
  // (`query` is reused for that too — see the panel below).
  const filtered = (options || []).filter(
    (o) =>
      !isSelected(o.value) &&
      (o.label || "").toLowerCase().includes(query.toLowerCase()),
  );

  const handleAdd = (item) => {
    try {
      if (multiple) {
        onChange([...(value || []), item]);
      } else {
        onChange(item);
        setOpen(false);
      }
      setQuery("");
    } catch (err) {
      console.error("Error adding autocomplete selection:", err);
    }
  };

  const handleRemove = (val) => {
    try {
      onChange((value || []).filter((x) => x.value !== val));
    } catch (err) {
      console.error("Error removing autocomplete selection:", err);
    }
  };

  const handleKeyDown = (e) => {
    // Multiple: value is only ever committed here, on Tab/Enter — typing
    // alone never adds a chip, so a user can type a full search phrase
    // before deciding to add it as a free-text option.
    // (Only reachable in autocomplete mode — dropdown mode never renders
    // the free-typing input this is attached to.)
    if (
      multiple &&
      (e.key === "Tab" || e.key === "Enter") &&
      query.trim() !== ""
    ) {
      e.preventDefault();
      if (!isSelected(query)) {
        handleAdd({ value: query, label: query });
      }
    }
  };

  // Single-select: every keystroke commits the typed text as the current
  // value immediately (free-text), in addition to still being overridable
  // by clicking a matching option from the dropdown list below.
  // (Only reachable in autocomplete mode.)
  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    if (!multiple) {
      onChange(val ? { value: val, label: val } : null);
    }
  };

  // Dropdown mode: clicking the field just toggles the panel open/closed,
  // like a normal <select> — no free text is ever typed into the field.
  const handleFieldClick = () => {
    if (!allowAutoComplete) setOpen((p) => !p);
  };

  return (
    <div ref={ref} className="relative w-full">
      <div
        onClick={!allowAutoComplete ? handleFieldClick : undefined}
        className={`w-full bg-slate-50 min-h-[39px] bg-[#f8fafa] rounded-lg pl-9 pr-3 py-1.5 flex flex-wrap items-center gap-1.5 transition-all ${
          !allowAutoComplete ? "cursor-pointer" : ""
        } ${
          open ? "border-[#08757C] ring-2 ring-[#08757C]/20 bg-white" : ""
        }${error ? "border-2 border-red-500" : "border border-slate-200"}`}
      >
        <span className="material-symbols-outlined text-[16px] text-[#9eabac] absolute left-3">
          {allowAutoComplete ? icon : "expand_more"}
        </span>

        {multiple &&
          value?.map((o) => (
            <span
              key={o.value}
              className="inline-flex items-center gap-1 bg-[#e6f4f4] text-[#08757C] text-xs font-semibold px-2 py-0.5 rounded-lg"
            >
              {o.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(o.value);
                }}
                className="hover:text-red-500"
              >
                ×
              </button>
            </span>
          ))}

        {allowAutoComplete ? (
          <input
            value={
              open
                ? query
                : !multiple && selectedObj
                  ? selectedObj.label
                  : query
            }
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            placeholder={
              !multiple && selectedObj ? selectedObj.label : placeholder
            }
            className="flex-1 min-w-[90px] h-7 bg-transparent text-sm font-medium text-[#181c1d] focus:outline-none placeholder:text-[#9eabac]"
          />
        ) : (
          // Plain-dropdown mode: read-only display, no typing — exactly
          // like a normal <select>. Value is only ever set by clicking an
          // option in the panel below.
          <span
            className={`flex-1 min-w-[90px] h-7 leading-7 text-sm font-medium truncate select-none ${
              !multiple && selectedObj ? "text-[#181c1d]" : "text-[#9eabac]"
            }`}
          >
            {!multiple && selectedObj ? selectedObj.label : placeholder}
          </span>
        )}

        {allowAutoComplete && !multiple && value && !open && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="text-[#9eabac] hover:text-red-500"
          >
            ×
          </button>
        )}
      </div>

      <DropdownPortal open={open} anchorRef={ref}>
        <div className="bg-white border border-[#e5e9e9] rounded-lg shadow-xl overflow-hidden">
          {!allowAutoComplete && (
            // Dropdown mode: the search box lives inside the panel
            // instead of the field itself, same as a normal <select>
            // with type-to-filter — the field never accepts free text.
            <div className="p-2 border-b border-[#f1f4f4]">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full h-8 bg-[#f1f4f4] rounded-lg px-2.5 text-xs font-medium border border-transparent focus:outline-none focus:ring-1 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#c0cbcc]"
              />
            </div>
          )}
          <div className="max-h-44 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#9eabac] px-3 py-2">
                {allowAutoComplete && query && multiple
                  ? `Press Tab to add "${query}"`
                  : "No options"}
              </p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleAdd(o)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#f0fafa] text-[#181c1d]"
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      </DropdownPortal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTAL DROPDOWN — a value/onChange/options dropdown that looks and
// behaves like Common/UIComponents.js's `Dropdown`, but renders its
// option panel through DropdownPortal (into document.body) instead of
// as a plain `position:absolute` child.
//
// WHY THIS EXISTS: `Dropdown` positions its panel with plain CSS
// `absolute`. That's fine on its own, but the moment it's placed
// inside a horizontally-scrolling container (e.g. a wide table
// wrapped in `overflow-x-auto`, like the Test Results table), the
// browser is forced to also clip the OTHER axis — per the CSS spec,
// an element can't have `overflow-x: auto` and `overflow-y: visible`
// at the same time; setting one axis to non-visible silently forces
// the other to `auto` too. So any part of the panel that extends
// below the scroll container's own height gets invisibly cut off —
// which is exactly the "dropdown options aren't visible" bug seen in
// the Test Results table.
//
// This component sidesteps the whole problem the same way
// `AutoComplete`/`MultiSelect` above already do: render the panel
// into `document.body` via a portal, `position: fixed` to the
// trigger's on-screen coordinates. It never touches the shared
// `Dropdown` component itself, so every OTHER page that already uses
// `Dropdown` (which isn't inside a clipping scroll container, so
// never had this bug) is completely unaffected.
// ─────────────────────────────────────────────────────────────
function PortalDropdown({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  error = "", // optional inline validation message, matched to other pages
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        !e.target.closest(".dp-portal-menu")
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = (options || []).filter((o) =>
    (o.label || "").toLowerCase().includes(search.toLowerCase()),
  );
  const selectedOption = options.find((o) => String(o.value) === String(value));

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={`w-full h-11 flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 text-sm font-medium text-left transition-all outline-none
          focus:ring-2 focus:ring-[#08757C]/20 focus:border-[#08757C]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${
            error
              ? "border-2 border-red-500"
              : open
                ? "border border-[#08757C] ring-2 ring-[#08757C]/20"
                : "border border-slate-200"
          }`}
      >
        <span className="flex-1 min-w-0 truncate">
          {selectedOption ? (
            <span className="text-[#181c1d]">{selectedOption.label}</span>
          ) : (
            <span className="text-[#9eabac]">{placeholder}</span>
          )}
        </span>
        <span
          className={`material-symbols-outlined text-[16px] text-[#9eabac] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          expand_more
        </span>
      </button>

      <DropdownPortal open={open} anchorRef={ref}>
        <div className="bg-white border border-[#e5e9e9] rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#f1f4f4]">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full h-8 bg-[#f1f4f4] rounded-lg px-2.5 text-xs font-medium border border-transparent focus:outline-none focus:ring-1 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#c0cbcc]"
            />
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#9eabac] px-3 py-2 text-center">
                No results found
              </p>
            ) : (
              filtered.map((o) => {
                const isSelected = String(o.value) === String(value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                      isSelected
                        ? "bg-[#08757C]/8 text-[#08757C] font-semibold"
                        : "text-[#181c1d] hover:bg-[#f0fafa]"
                    }`}
                  >
                    {o.label}
                    {isSelected && (
                      <span className="material-symbols-outlined text-[#08757C] text-[16px]">
                        check
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </DropdownPortal>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// True multi-select dropdown
function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Select options…",
  icon = "checklist",
  error = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        !e.target.closest(".dp-portal-menu")
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const isChecked = (val) => value?.some((v) => v.value === val);

  const toggleOption = (opt) => {
    try {
      if (isChecked(opt.value)) {
        onChange(value.filter((v) => v.value !== opt.value));
      } else {
        onChange([...(value || []), opt]);
      }
    } catch (err) {
      console.error("Error toggling multi-select option:", err);
    }
  };

  const handleRemove = (val) => {
    try {
      onChange(value.filter((v) => v.value !== val));
    } catch (err) {
      console.error("Error removing multi-select option:", err);
    }
  };

  const filteredOptions = (options || []).filter((o) =>
    (o.label || "").toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full bg-slate-50 min-h-[40px] bg-[#f8fafa] rounded-lg pl-9 pr-3 py-1.5 flex flex-wrap items-center gap-1.5 transition-all text-left ${
          open ? "border-[#08757C] ring-2 ring-[#08757C]/20 bg-white" : ""
        }${error ? "border-2 border-red-500" : "border border-slate-200"}`}
      >
        <span className="material-symbols-outlined text-[16px] text-[#9eabac] absolute left-3">
          {icon}
        </span>

        {value?.length === 0 ? (
          <span className="text-sm font-medium text-[#9eabac]">
            {placeholder}
          </span>
        ) : (
          value?.map((o) => (
            <span
              key={o.value}
              className="inline-flex items-center gap-1 bg-[#e6f4f4] text-[#08757C] text-xs font-semibold px-2 py-0.5 rounded-lg"
            >
              {o.label}
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(o.value);
                }}
                className="hover:text-red-500"
              >
                ×
              </span>
            </span>
          ))
        )}

        <span className="material-symbols-outlined text-[16px] text-[#9eabac] ml-auto flex-shrink-0">
          {open ? "expand_less" : "expand_more"}
        </span>
      </button>

      <DropdownPortal open={open} anchorRef={ref}>
        <div className="bg-white border border-[#e5e9e9] rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#f1f4f4]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full h-8 bg-[#f1f4f4] rounded-lg px-2.5 text-xs font-medium border border-transparent focus:outline-none focus:ring-1 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#c0cbcc]"
            />
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="text-xs text-[#9eabac] px-3 py-2">No options</p>
            ) : (
              filteredOptions.map((o) => {
                const checked = isChecked(o.value);
                return (
                  <label
                    key={o.value}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[#f0fafa] cursor-pointer"
                  >
                    <span
                      className={`h-4 w-4 flex-shrink-0 rounded-md border flex items-center justify-center transition-all ${
                        checked
                          ? "bg-[#08757C] border-[#08757C]"
                          : "bg-white border-[#c0cbcc]"
                      }`}
                    >
                      {checked && (
                        <span
                          className="material-symbols-outlined text-white text-[13px]"
                          style={{ fontVariationSettings: "'FILL' 1" }}
                        >
                          check
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={checked}
                      onChange={() => toggleOption(o)}
                    />
                    <span className="text-[#181c1d] font-medium">
                      {o.label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          {value?.length > 0 && (
            <div className="px-3 py-2 border-t border-[#f1f4f4] flex justify-between items-center bg-[#fafcfc]">
              <span className="text-[10.5px] text-[#9eabac] font-semibold">
                {value.length} selected
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10.5px] font-bold text-red-500 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </DropdownPortal>
    </div>
  );
}

// Modal preview shown when "Preview Email" is clicked
function EmailPreviewModal({ open, onClose, mp, subject, rows = [] }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "fadeUp .2s ease" }}
      >
        <div
          className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[#e5e9e9]"
          style={{ background: "linear-gradient(to right,#e6f4f4,#f7fafa)" }}
        >
          <div className="h-8 w-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-[#08757C] flex-shrink-0">
            <span className="material-symbols-outlined text-[16px]">mail</span>
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#181c1d]">Email Preview</h3>
            <p className="text-[10.5px] text-[#6e797a]">
              This is how the notification will look
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto h-7 w-7 flex items-center justify-center rounded-lg text-[#6e797a] hover:bg-[#f1f4f4] transition-all flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
        <div className="p-5 space-y-3 text-[12.5px] max-h-[60vh] overflow-y-auto">
          <div className="flex gap-2">
            <span className="w-16 flex-shrink-0 text-[#9eabac] font-bold uppercase text-[10px] pt-0.5">
              To
            </span>
            <span className="font-semibold text-[#181c1d]">
              lab-team@enterprise-lab.com
            </span>
          </div>
          <div className="flex gap-2">
            <span className="w-16 flex-shrink-0 text-[#9eabac] font-bold uppercase text-[10px] pt-0.5">
              MP No
            </span>
            <span className="font-semibold text-[#181c1d]">
              {mp?.MPNo ?? "—"}
            </span>
          </div>
          <div className="flex gap-2">
            <span className="w-16 flex-shrink-0 text-[#9eabac] font-bold uppercase text-[10px] pt-0.5">
              Subject
            </span>
            <span className="font-semibold text-[#181c1d]">{subject}</span>
          </div>
          <div className="border-t border-[#f1f4f4] pt-3 space-y-1.5">
            {rows?.length === 0 ? (
              <p className="text-[#9eabac] text-center py-3">
                No data entered yet
              </p>
            ) : (
              rows?.map((r, i) =>
                // A row with an empty value and a label wrapped in "— … —"
                // is a section header used by the combined/overall preview.
                r.value === "" && /^—.*—$/.test(r.label || "") ? (
                  <div
                    key={i}
                    className="pt-2 first:pt-0 text-[10px] font-black uppercase tracking-widest text-[#08757C]"
                  >
                    {r.label}
                  </div>
                ) : (
                  <div key={i} className="flex justify-between gap-3">
                    <span className="text-[#6e797a] font-medium">
                      {r.label}
                    </span>
                    <span className="text-[#181c1d] font-semibold text-right">
                      {r.value || "—"}
                    </span>
                  </div>
                ),
              )
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#e5e9e9] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 h-9 rounded-xl border border-[#e5e9e9] text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// "System Status / Active" style toggle row
function ToggleSwitch({ checked, onChange, label, sub, required }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        {label && (
          <p className="text-[11.5px] font-bold text-[#3e494a] truncate flex items-center gap-1">
            <span>{label}</span>
            {required && <span className="text-red-500 font-bold">*</span>}
          </p>
        )}
        {sub && (
          <p className="text-[9.5px] font-semibold text-[#9eabac] uppercase tracking-wide truncate">
            {sub}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative h-6 w-11 rounded-full transition-colors ${
            checked ? "bg-emerald-200" : "bg-[#e5e9e9]"
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full shadow transition-all ${
              checked
                ? "left-[22px] bg-emerald-500"
                : "left-0.5 bg-white border border-[#d5dbdb]"
            }`}
          />
        </button>
        <span
          className={`text-[10px] font-black uppercase tracking-wide w-7 ${
            checked ? "text-emerald-600" : "text-[#c0cbcc]"
          }`}
        >
          {checked ? "Yes" : "No"}
        </span>
      </div>
    </div>
  );
}

// Pill-style segmented control for small enum choices (e.g. Pass/Fail/NA)
function SegmentedSelect({ value, onChange, options, error }) {
  return (
    <div
      className={`inline-flex flex-wrap rounded-lg border border-[#e5e9e9] bg-[#f8fafa] p-1 gap-1 ${
        error
          ? "border-2 border-red-500 rounded-lg"
          : "border border-slate-200 rounded-lg"
      }`}
    >
      {options?.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3.5 h-8 rounded-lg text-[11.5px] font-bold transition-all ${
            value === o.value
              ? "text-white shadow-sm"
              : "text-[#6e797a] hover:bg-white"
          }`}
          style={value === o.value ? { background: BRAND } : {}}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Column-search input — matched to the standard field styling used on the
// other pages (white background, slate border, rounded-lg, primary focus
// ring) instead of the old borderless tinted pill.
function ColSearch({ value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search…"
      className="w-full h-9 bg-white rounded-lg px-3 text-[12px] text-[#181c1d] border border-[#bdc9c9] focus:outline-none focus:ring-2 focus:ring-[#005b60] focus:border-transparent transition-all placeholder:text-[#9eabac]"
    />
  );
}

// Header cell — matched to the shared ConfigTable header used on every
// other list page (neutral #f1f4f4 background, dark slate uppercase
// label). It now carries its OWN background so it no longer depends on a
// teal gradient being set on the parent <tr>.
function TH({ children, w }) {
  return (
    <th
      style={{ width: w }}
      scope="col"
      className="bg-[#f1f4f4] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[#3e494a] whitespace-nowrap border-b border-[#ebeeee]"
    >
      {children}
    </th>
  );
}

// Body cell — matched to the shared ConfigTable body cell used on every
// other list page (13px dark text, #ebeeee divider, comfortable padding).
function TD({ children, cls = "", style }) {
  return (
    <td
      style={style}
      className={`px-4 py-3 border-b border-[#ebeeee] text-[13px] text-[#181c1d] ${cls}`}
    >
      {children}
    </td>
  );
}

function Empty({ icon = "inbox", msg = "No records", sub = "" }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-[#9eabac]">
      <span className="material-symbols-outlined text-[36px] mb-2">{icon}</span>
      <p className="text-sm font-bold text-[#3e494a]">{msg}</p>
      {sub && <p className="text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

// Toast supports a `type` of "success" (default, green) or "error" (red),
// so the same component can surface both save confirmations and
// caught-exception messages without introducing a second component.
function Toast({ msg, show, type = "success" }) {
  if (!show) return null;
  const isError = type === "error";
  return (
    <div
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2.5 ${
        isError ? "bg-red-500" : "bg-emerald-500"
      } text-white px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold pointer-events-none`}
      style={{ animation: "fadeUp .25s cubic-bezier(.4,0,.2,1)" }}
    >
      <span
        className="material-symbols-outlined text-[18px]"
        style={{ fontVariationSettings: "'FILL' 1" }}
      >
        {isError ? "error" : "check_circle"}
      </span>
      {msg}
    </div>
  );
}

// FormActions: Preview Email / Save / Submit. Submit calls `onSubmit`
// (the cross-tab, page-wide validation + submit flow), falling back to
// `onSave` if a caller doesn't provide one. The Submit button uses a
// darker shade of the same brand gradient as the rest of the page so it
// reads as "the important brand action", not an unrelated color.
function FormActions({ onSave, saving, onPreview, onSubmit }) {
  return (
    <div className="flex flex-wrap justify-end gap-3 pt-3 border-t border-[#e5e9e9] mt-4">
      {/* <button
        onClick={onPreview}
        className="flex items-center gap-2 px-4 h-10 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
      >
        <span className="material-symbols-outlined text-[16px]">mail</span>
        Preview Email
      </button> */}
      <button
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-2 px-5 h-10 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all disabled:opacity-60"
        style={{ background: BRAND }}
      >
        <span className="material-symbols-outlined text-[16px]">
          {saving ? "progress_activity" : "save"}
        </span>
        {saving ? "Saving…" : "Save"}
      </button>
      <button
        onClick={onSubmit ?? onSave}
        disabled={saving}
        className="flex items-center gap-2 px-5 h-10 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all disabled:opacity-60"
        style={{ background: SUBMIT_GRAD }}
      >
        <span className="material-symbols-outlined text-[16px]">send</span>
        Submit
      </button>
    </div>
  );
}

export {
  toArray,
  BRAND,
  BRAND_DARK,
  BRAND_GRAD,
  SUBMIT_GRAD,
  iCls,
  eiCls,
  TABS,
  STATUS_BADGE,
  Lbl,
  Badge,
  Btn,
  ExpandToggle,
  SectionIcon,
  Section,
  FLbl,
  iconInputCls,
  IconField,
  useDropdownPosition,
  DropdownPortal,
  AutoComplete,
  PortalDropdown,
  MultiSelect,
  EmailPreviewModal,
  ToggleSwitch,
  SegmentedSelect,
  ColSearch,
  TH,
  TD,
  Empty,
  Toast,
  FormActions,
};
